import { Component, OnDestroy, OnInit } from '@angular/core';
import { AuthFacadeService } from '@auth/facades/auth-facade.service';
import { User } from '@auth/models/user.model';
import { AuthService } from '@auth/services/auth.service';
import { SecurityService } from '@core/services/security.service';
import { SplashReadyService } from '@shared/services/splash-ready.service';
import { hatUebergabeStattgefunden } from './core/auth/auth-url-bootstrap';
import { PrimeNGConfig } from 'primeng/api';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Startbild «Beleglauf» - die Anwendung läuft von Anfang an DARUNTER.
 *
 * MIN ist die Zeit, bis die Geschichte erzählt ist (Kamerafahrt steht, Titel da,
 * Kette voll). Sind die Daten vorher da, verschwindet das Startbild genau bei
 * MIN - nicht später. MAX ist die Reissleine: Bleibt das Signal aus, geht es
 * trotzdem weg und die Seite zeigt ihren eigenen Ladezustand.
 */
const STARTBILD_MIN_MS = 2800;
const STARTBILD_MAX_MS = 15000;
/** Muss der `transition` von `:host` in startbild.component.css entsprechen. */
const STARTBILD_AUSBLENDEN_MS = 450;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit, OnDestroy {
  user$: Observable<User | null>;

  /** 'zeigen' → 'ausblenden' → 'weg'. */
  public startbild: 'zeigen' | 'ausblenden' | 'weg' = 'weg';

  private readonly abos = new Subscription();
  private readonly zeitgeber: ReturnType<typeof setTimeout>[] = [];
  /** Kam dieser Aufruf über die Portal-Übergabe? Einmal beim Start ermittelt. */
  private readonly uebergabe = hatUebergabeStattgefunden();

  constructor(
    private authService: AuthService,
    private authFacade: AuthFacadeService,
    private security: SecurityService,
    private config: PrimeNGConfig,
    private splashReady: SplashReadyService,
    private router: Router
  ) {
    this.user$ = this.authFacade.user$;
    // Im Konstruktor entschieden, nicht in ngOnInit: Das Startbild soll mit dem
    // ersten Bild da sein, nicht einen Wimpernschlag später.
    // ⚠️ `router.url` steht hier noch auf '/', die erste Navigation hat nicht
    // stattgefunden. `location.pathname` stimmt schon.
    this.startbild =
      this.uebergabe || this.startbildErwuenscht(window.location.pathname)
        ? 'zeigen'
        : 'weg';
  }

  ngOnInit(): void {
    const authUserToken = this.authService.getAuthUserToken();
    this.setTranslation();

    if (authUserToken) {
      this.authFacade.setAuthUser(authUserToken);
    }

    this.startbildSteuern();
  }

  ngOnDestroy(): void {
    this.abos.unsubscribe();
    this.zeitgeber.forEach((z) => clearTimeout(z));
  }

  /**
   * Auf der Anmeldeseite kein Startbild: Dort wird nichts geladen, worauf es
   * warten könnte - es stünde nur im Weg. Ebenso beim Absprung auf ein
   * einzelnes Dokument: Wer einen Link anklickt, will das PDF sehen.
   */
  private startbildErwuenscht(url: string): boolean {
    return (
      !url.startsWith('/auth') &&
      !url.startsWith('/startbild-vorschau') &&
      !url.startsWith('/dokument')
    );
  }

  /** Die Belegseite ist die einzige, die das Fertig-Signal gibt. */
  private wartetAufDaten(url: string): boolean {
    return url === '/' || url.startsWith('/?');
  }

  private startbildSteuern(): void {
    if (this.startbild === 'weg') {
      return;
    }

    let minVorbei = false;
    let bereit = false;
    let schonAusgeblendet = false;

    const ausblenden = (): void => {
      if (schonAusgeblendet) {
        return;
      }
      schonAusgeblendet = true;
      this.startbild = 'ausblenden';
      this.zeitgeber.push(
        setTimeout(() => (this.startbild = 'weg'), STARTBILD_AUSBLENDEN_MS)
      );
    };
    const pruefen = (): void => {
      if (minVorbei && bereit) {
        ausblenden();
      }
    };

    this.zeitgeber.push(
      setTimeout(() => {
        minVorbei = true;
        pruefen();
      }, STARTBILD_MIN_MS)
    );
    this.zeitgeber.push(setTimeout(ausblenden, STARTBILD_MAX_MS));

    this.abos.add(
      this.splashReady.bereit$().subscribe((istBereit) => {
        bereit = istBereit;
        pruefen();
      })
    );

    // Ein Fehler hebt die Mindestdauer auf: Die Meldung darunter soll sofort
    // sichtbar werden, nicht erst wenn die Geschichte zu Ende erzählt ist.
    this.abos.add(
      this.splashReady.fehler$().subscribe((istFehler) => {
        if (istFehler) {
          ausblenden();
        }
      })
    );

    this.abos.add(
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe((e) => {
          const url = e.urlAfterRedirects;
          // Bei einer Übergabe ist /auth nur die Durchgangsstation: weder ein
          // Fehler noch ein Ziel. Abwarten, wohin sie führt.
          if (this.uebergabe && url.startsWith('/auth')) {
            return;
          }
          // Landet der Wächter auf der Anmeldeseite, ist nichts mehr zu
          // erwarten - das Startbild würde die Anmeldung sonst bis zur
          // Obergrenze verdecken.
          if (!this.startbildErwuenscht(url)) {
            this.splashReady.melden('fehler');
            return;
          }
          // Nur die Belegseite meldet 'daten'. Wer direkt auf einer anderen
          // Seite landet, wartet sonst bis zur Obergrenze auf ein Signal, das
          // dort niemand gibt.
          if (!this.wartetAufDaten(url)) {
            this.splashReady.melden('daten');
          }
        })
    );
  }

  setTranslation(): void {
    this.config.setTranslation({
      firstDayOfWeek: 1,
      dayNames: [
        'Sonntag',
        'Montag',
        'Dienstag',
        'Mittwoch',
        'Donnerstag',
        'Freitag',
        'Samstag',
      ],
      dayNamesShort: ['Son', 'Mon', 'Die', 'Mit', 'Don', 'Fre', 'Sam'],
      dayNamesMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
      monthNames: [
        'Januar',
        'Februar',
        'März',
        'April',
        'Mai',
        'Juni',
        'Juli',
        'August',
        'September',
        'Oktober',
        'November',
        'Dezember',
      ],
      weekHeader: 'W',
      monthNamesShort: [
        'Jan',
        'Feb',
        'Mär',
        'Apr',
        'Mai',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Okt',
        'Nov',
        'Dez',
      ],
      today: 'Heute',
    });
  }

  /**
   * Abmelden von Hand.
   *
   * Vorher sprang diese Methode zum Portal und räumte danach auf - der Reset
   * lief also gegen den Seitenwechsel und verlor. Ausserdem blieben JWT und
   * Zeichen-Token liegen, weil nur das Benutzerobjekt entfernt wurde.
   *
   * Jetzt macht das der SecurityService in der richtigen Reihenfolge. Bewusst
   * OHNE Rücksprungziel: Wer abmeldet, will hinaus und nicht nach der nächsten
   * Anmeldung wieder hier landen.
   */
  logout(): void {
    this.security.forceLogout('Sie wurden abgemeldet.', null)
  }
}
